import React from "react";

import ChildrensBookManuscriptIllustrationContext from "./ChildrensBookManuscriptIllustrationContext";


function clean(value) {
  return String(
    value ?? ""
  ).trim();
}


function validPageNumber(
  value
) {
  const number =
    Number(value);

  return (
    Number.isInteger(number) &&
    number > 0
  )
    ? number
    : null;
}


function pageCompositionForUnit(
  unit = {}
) {
  const source =
    (
      unit?.pageComposition &&
      typeof unit.pageComposition ===
        "object" &&
      !Array.isArray(
        unit.pageComposition
      )
    )
      ? unit.pageComposition
      : {};

  return {
    title:
      clean(
        source.title
      ),

    subtitle:
      clean(
        source.subtitle
      ),

    caption:
      clean(
        source.caption
      ),

    creditLine:
      clean(
        source.creditLine
      ),
  };
}


export function manuscriptFullPageSingleLayoutApplies(
  unit = {}
) {
  const unitType =
    clean(
      unit?.unitType
    ).toLowerCase();

  const illustrationPlan =
    clean(
      unit?.illustrationPlan
    ).toLowerCase();

  return (
    unitType ===
      "single_page" &&
    illustrationPlan ===
      "full_page"
  );
}


export default function ChildrensBookManuscriptFullPageSingleLayout({
  lang = "en",
  unit,
  pageNumber,
  childrensBookAssets = [],
  onLoadChildrensBookAssetFile,
  plannedTextRequired = false,
  attachedWordCount = 0,
  previousLabel = "",
  nextLabel = "",
  previousDisabled = false,
  nextDisabled = false,
  onPrevious,
  onNext,
}) {
  if (
    !manuscriptFullPageSingleLayoutApplies(
      unit
    )
  ) {
    return null;
  }

  const isFR =
    String(lang)
      .toLowerCase() ===
      "fr";

  const physicalPage =
    validPageNumber(
      pageNumber
    );

  const pageComposition =
    pageCompositionForUnit(
      unit
    );

  const hasPageHeading =
    Boolean(
      pageComposition.title ||
      pageComposition.subtitle
    );

  const hasPageFooter =
    Boolean(
      pageComposition.caption ||
      pageComposition.creditLine
    );

  const words =
    Math.max(
      0,
      Number(
        attachedWordCount
      ) || 0
    );

  const hasCapacityConflict =
    Boolean(
      plannedTextRequired
    );

  const hasAttachedText =
    words > 0;

  return (
    <div
      className="ns-nbb-editing-panel ns-nbb-manuscript-full-page-single-shell"
      data-testid="manuscript-full-page-single-layout"
      style={{
        display: "grid",
        gap: 16,
        marginTop: 18,
      }}
    >
      <div
        className="ns-nbb-manuscript-unit-nav"
        data-testid="manuscript-full-page-single-nav"
        style={{
          display: "flex",
          justifyContent:
            "space-between",
          alignItems:
            "center",
          gap: 12,
          flexWrap:
            "wrap",
          paddingBottom:
            12,
          borderBottom:
            "1px solid #e2e8f0",
        }}
      >
        <button
          className="ns-button ns-button-outline"
          type="button"
          disabled={
            previousDisabled
          }
          onClick={
            onPrevious
          }
        >
          {previousLabel ||
            (
              isFR
                ? "Page précédente"
                : "Previous Page"
            )}
        </button>

        <strong>
          {isFR
            ? `Affichage — Page ${physicalPage || ""}`
            : `Viewing Page ${physicalPage || ""}`}
        </strong>

        <button
          className="ns-button ns-button-outline"
          type="button"
          disabled={
            nextDisabled
          }
          onClick={
            onNext
          }
        >
          {nextLabel ||
            (
              isFR
                ? "Page suivante"
                : "Next Page"
            )}
        </button>
      </div>

      {hasCapacityConflict ? (
        <div
          role="alert"
          data-testid="manuscript-full-page-single-text-conflict"
          style={{
            padding: 14,
            borderRadius: 12,
            border:
              "1px solid rgba(217,119,6,0.32)",
            background:
              "#fffbeb",
            color:
              "#92400e",
            lineHeight:
              1.55,
          }}
        >
          <strong>
            {isFR
              ? "Cette page est prévue pour du texte et une illustration pleine page."
              : "This page is planned for manuscript text and a full-page illustration."}
          </strong>

          <p
            style={{
              margin:
                "7px 0 0",
            }}
          >
            {isFR
              ? "Une illustration pleine page utilise toute la zone de manuscrit de cette page. Dans la Structure du livre, choisissez « Aucun texte » ou une illustration plus petite avant la publication."
              : "A full-page illustration uses this page’s entire manuscript area. In Book Structure, choose “No text” or a smaller illustration before publication."}
          </p>
        </div>
      ) : null}

      {hasAttachedText ? (
        <div
          role="alert"
          data-testid="manuscript-full-page-single-attached-text-warning"
          style={{
            padding: 14,
            borderRadius: 12,
            border:
              "1px solid rgba(185,28,28,0.24)",
            background:
              "#fff7f7",
            color:
              "#991b1b",
            lineHeight:
              1.55,
          }}
        >
          <strong>
            {isFR
              ? `${words} mot(s) de manuscrit restent attachés à cette unité.`
              : `${words} manuscript word(s) remain attached to this planning unit.`}
          </strong>

          <p
            style={{
              margin:
                "7px 0 0",
            }}
          >
            {isFR
              ? "Ils sont conservés dans l’état du projet, mais ils ne sont pas affichés sur cette page pleine illustration. Modifiez d’abord la mise en page dans la Structure du livre avant de continuer à les éditer."
              : "They remain preserved in project state, but they are not displayed on this full-page illustration. Change the layout in Book Structure before continuing to edit them."}
          </p>
        </div>
      ) : null}

      <article
        data-testid="manuscript-single-page-panel"
        data-page-role="illustration"
        aria-label={
          isFR
            ? `Page ${physicalPage || ""} — Illustration pleine page`
            : `Page ${physicalPage || ""} — Full-page illustration`
        }
        style={{
          minHeight: 640,
          padding: 24,
          borderRadius: 16,
          border:
            "1px solid #94a3b8",
          background:
            "#ffffff",
          display: "flex",
          flexDirection:
            "column",
          gap: 14,
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent:
              "space-between",
            alignItems:
              "center",
            gap: 12,
          }}
        >
          <strong
            style={{
              fontSize: 22,
              color:
                "#172033",
            }}
          >
            {isFR
              ? `Page ${physicalPage || ""}`
              : `Page ${physicalPage || ""}`}
          </strong>

          <strong
            style={{
              color:
                "#334155",
            }}
          >
            {isFR
              ? "Illustration"
              : "Illustration"}
          </strong>
        </div>

        <div
          data-testid="manuscript-composed-illustration-page"
          style={{
            display: "flex",
            flex: 1,
            minHeight: 0,
            flexDirection:
              "column",
            gap: 14,
          }}
        >
          {hasPageHeading ? (
            <header
              data-testid="manuscript-page-composition-heading"
              style={{
                textAlign:
                  "center",
                padding:
                  "2px 10px 0",
              }}
            >
              {pageComposition
                .title ? (
                <h3
                  data-testid="manuscript-page-composition-title"
                  style={{
                    margin: 0,
                    fontFamily:
                      "Georgia, 'Times New Roman', serif",
                    fontSize:
                      "clamp(23px, 2.5vw, 34px)",
                    lineHeight:
                      1.16,
                    color:
                      "#172033",
                  }}
                >
                  {
                    pageComposition
                      .title
                  }
                </h3>
              ) : null}

              {pageComposition
                .subtitle ? (
                <p
                  data-testid="manuscript-page-composition-subtitle"
                  style={{
                    margin:
                      pageComposition
                        .title
                        ? "7px 0 0"
                        : 0,
                    fontSize: 16,
                    lineHeight:
                      1.45,
                    color:
                      "#475569",
                  }}
                >
                  {
                    pageComposition
                      .subtitle
                  }
                </p>
              ) : null}
            </header>
          ) : null}

          <div
            style={{
              flex: 1,
              minHeight: 0,
            }}
          >
            <ChildrensBookManuscriptIllustrationContext
              lang={lang}
              unit={unit}
              childrensBookAssets={
                childrensBookAssets
              }
              onLoadChildrensBookAssetFile={
                onLoadChildrensBookAssetFile
              }
              presentation="page"
            />
          </div>

          {hasPageFooter ? (
            <footer
              data-testid="manuscript-page-composition-footer"
              style={{
                display: "grid",
                gap: 5,
                textAlign:
                  "center",
                padding:
                  "0 12px 2px",
              }}
            >
              {pageComposition
                .caption ? (
                <div
                  data-testid="manuscript-page-composition-caption"
                  style={{
                    fontSize: 14,
                    lineHeight:
                      1.45,
                    color:
                      "#334155",
                  }}
                >
                  {
                    pageComposition
                      .caption
                  }
                </div>
              ) : null}

              {pageComposition
                .creditLine ? (
                <div
                  data-testid="manuscript-page-composition-credit"
                  style={{
                    fontSize: 12,
                    lineHeight:
                      1.4,
                    color:
                      "#64748b",
                  }}
                >
                  {
                    pageComposition
                      .creditLine
                  }
                </div>
              ) : null}
            </footer>
          ) : null}
        </div>
      </article>
    </div>
  );
}
